import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

/**
 * Consistent empty-state block with an optional CTA that turns a dead-end into
 * a conversion touchpoint. Used across Community, Leaderboard, CopyTrading,
 * Dashboard "no positions", etc.
 *
 * Props:
 *   icon      – optional lucide icon component to render at top (colored)
 *   title     – short headline (e.g. "No trades yet")
 *   body      – supporting sub-line
 *   ctaLabel  – button label ("Open the Terminal")
 *   ctaTo     – react-router path
 *   ctaTestId – testid for the CTA button
 *   testId    – testid for the wrapping empty state
 */
export default function EmptyState({
    icon: Icon,
    title,
    body,
    ctaLabel,
    ctaTo,
    ctaTestId,
    testId = "empty-state",
    dense = false,
}) {
    return (
        <div
            className={`flex flex-col items-center justify-center text-center ${dense ? "py-10" : "py-16"} px-6`}
            data-testid={testId}
        >
            {Icon && (
                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                </div>
            )}
            {title && (
                <div className="text-lg font-extrabold tracking-tight text-foreground mb-1">
                    {title}
                </div>
            )}
            {body && (
                <div className="text-sm text-muted-foreground max-w-sm leading-relaxed mb-4">
                    {body}
                </div>
            )}
            {ctaLabel && ctaTo && (
                <Button asChild size="sm" className="gap-1.5" data-testid={ctaTestId}>
                    <Link to={ctaTo}>
                        {ctaLabel}
                        <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </Button>
            )}
        </div>
    );
}
